import { CraApiResponse, CadastroCraApiRequest, InstrumentoFinanceiro, TipoRegistroInstrumentoFinanceiro, ReservarUltimoNumeroDaEmissaoRequest } from 'common/genericTypes';
import FormularioCRA from 'pages/cra/registro/formulario';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { RootState } from 'store';
import {
  resetCraDetails,
  updateCraCaracteristicas,
  updateField,
  resetMetadata,
} from 'store/modules/cra/caracteristicas';
import { resetCriteriosRemuneracao, updateCriteriosRemuneracao } from 'store/modules/cra/criteriosRemuneracao';
import { resetEventos, updateEventos } from 'store/modules/cra/eventos';
import { resetDocuments, updateDocuments } from 'store/modules/cra/uploadDocumentos';
import { updateId, resetEmissor } from 'store/modules/cra/emissor';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import CraParser from 'utils/parsers/craParser';
import { addAlert } from 'store/modules/snackBar';
import { highlightErrors, typeOfRegister } from 'store/modules/cra';

type ComponentParams = { craId?: string };
type ComponentState = { craRascunho?: CraApiResponse; acao?: TipoRegistroInstrumentoFinanceiro };

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const Cra: React.FC<Props> = (props) => {
  const { craId } = props.match.params;
  const craRascunho = props.location.state?.craRascunho;

  const history = useHistory();
  const dispatch = useDispatch();
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const caracteristicas = useSelector((state: RootState) => state.craCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.craCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.craCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.craCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.craCadastro.emissor);
  
  const craApi = useSelector((state: RootState) => state.craApi);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigo, setCodigo] = useState<string>('');

  const submitPutCra = async (
    craId: string,
    cra: CadastroCraApiRequest
  ) => {
    let result = { codigo: '', ticket: '' };
    try {
      const res = await craApi.putCra(dispatch, craId, cra, true);
      result = { codigo: res.codigo, ticket: res.ticket };
    } catch (error) { }
    return result;
  };

  const submitPostCra = async (): Promise<CraApiResponse> => {
    try {
      const cra: CadastroCraApiRequest = CraParser.stateToCadastroCraApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );
      return await craApi.postCra(dispatch, cra, true);
    } catch (error) {
      return {} as CraApiResponse;
    }
  };

  const submitCraDocument = async (
    craId: string,
    emissionType: string,
    file: File
  ): Promise<UploadDocResponse[]> => {
    try {
      return craApi.putUploadDoc(dispatch, craId, emissionType, file, true);
    } catch (error) {
      return [];
    }
  };

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Cra,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  const reserveBeforeHandleSubmit = async (handleSubmit: () => Promise<{ codigo: string; ticket: string } | undefined>): Promise<{ codigo: string; ticket: string } | undefined> => {
    const numeroEmissaoReservado = await reservarNumeroEmissao();
    if (!numeroEmissaoReservado) return;
    return await handleSubmit();
  }

  const handleEditSubmit = async () => {
    if (!craId) return { codigo: '', ticket: '' };

    const cra: CadastroCraApiRequest = CraParser.stateToCadastroCraApiRequest(
      caracteristicas,
      criteriosRemuneracao,
      eventos,
      uploadDocumentos,
      emissor
    );

    const craResponse = await submitPutCra(craId, cra);

    for (const document of uploadDocumentos.documents) {
      if (document.file) {
        const uploadResponse = await submitCraDocument(craId, document.type, document.file);
        if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
      }
    }

    if (caracteristicas.id) {
      for (const documentToDelete of uploadDocumentos.documentsToDelete) {
        documentToDelete.remoteFileInfo?.id &&
          (await craApi.deleteUploadDoc(dispatch, craId, documentToDelete.remoteFileInfo.id, true));
      }
    }

    return craResponse;
  };

  const handleRegisterSubmit = async () => {
    const uploadedCra = await submitPostCra();

    if (uploadedCra.id) {
      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          await submitCraDocument(uploadedCra.craId, document.type, document.file);
        }
      }
    }

    return { codigo: '', ticket: uploadedCra.ticket };
  };

  const handleAditamentoSubmit = async (): Promise<{ codigo: string, ticket: string } | undefined> => {
    try {
      if (codigo) {
        return { codigo, ticket: '' };
      } else throw 'ID do CRA não identificado';
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnToList = () => {
    history.push('/solicitacoes');
  };
  
  const handleSalvarRascunho = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return '';
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return '';
    }
    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Cra,
        externalId: participanteId,
      };
      
      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return '';
    }
    let draftId = '';
    try {
      const rascunho = CraParser.stateToCadastroCraApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );

      const documents = uploadDocumentos.documents
        .filter((doc) => !!doc.file)
        .map((doc) => ({ file: doc.file!, emissionType: doc.type }));

      const result = caracteristicas.draftId
        ? await instrumentoApi.putRascunho(
          dispatch,
          InstrumentoFinanceiro.Cra,
          caracteristicas.draftId,
          rascunho,
          documents,
          true
        )
        : await instrumentoApi.postRascunho(
          dispatch,
          InstrumentoFinanceiro.Cra,
          rascunho,
          documents,
          true
        );
      setCodigo(result.codigo);
      draftId = result.draftId;
      dispatch(updateField({ name: 'draftId', value: draftId }));

      dispatch(addAlert({ message: `Rascunho salvo com sucesso. Código do Instrumento Financeiro ${result.codigo}`, severity: 'success' }));
    } catch (error) { }
    return draftId;
  };

  const [typeOfFormulario, setTypeOfFormulario] = useState<TipoRegistroInstrumentoFinanceiro>(TipoRegistroInstrumentoFinanceiro.Cadastro);

  const defineTypeOfRegister = async (): Promise<void> => {
    if (craId) {
      if (props.location.state?.acao) {
        setTypeOfFormulario(props.location.state.acao);
        dispatch(typeOfRegister(props.location.state.acao));
      }
    } else {
      setTypeOfFormulario(TipoRegistroInstrumentoFinanceiro.Cadastro);
      dispatch(typeOfRegister(TipoRegistroInstrumentoFinanceiro.Cadastro));
    }
  };

  useEffect(() => {
    dispatch(resetEmissor());
    dispatch(resetMetadata())
    dispatch(resetCraDetails());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(highlightErrors(false));
    defineTypeOfRegister();
  }, []);

  useEffect(() => {
    if (craId) {
      craApi
        .getCraDetalhes(dispatch, craId, true)
        .then((response) => {
          if (response) {
            setCodigo(response.codigo);
            dispatch(updateId(response.emissorId ?? ''));
            dispatch(updateCraCaracteristicas(CraParser.apiResponseToCaracteristicasState(response)));
            dispatch(updateCriteriosRemuneracao(CraParser.apiResponseToCriteriosRemuneracaoState(response)));
            dispatch(updateEventos(CraParser.apiResponseToEventosState(response)));
            dispatch(updateDocuments(CraParser.apiResponseToCraDocumentsState(response)));
          }
        })
        .catch(() => { });
    } else if (craRascunho) {
      dispatch(updateId(craRascunho.emissorId ?? ''));
      dispatch(updateCraCaracteristicas(CraParser.apiResponseToCaracteristicasState(craRascunho, false)));
      dispatch(updateCriteriosRemuneracao(CraParser.apiResponseToCriteriosRemuneracaoState(craRascunho)));
      dispatch(updateEventos(CraParser.apiResponseToEventosState(craRascunho)));
      dispatch(updateDocuments(CraParser.apiResponseToCraDocumentsState(craRascunho)));
    }
    defineTypeOfRegister();
  }, [craApi, craId, craRascunho, dispatch]);

  const component = () => {
    switch (typeOfFormulario) {
      case TipoRegistroInstrumentoFinanceiro.Cadastro:
        return (
          <div>
            <FormularioCRA
              craId = {craId}
              title={`Cadastro CRA`}
              handleSubmit={() => reserveBeforeHandleSubmit(handleRegisterSubmit)}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Edicao:
        return (
          <div>
            <FormularioCRA
              craId = {craId}
              title={`Editar CRA`}
              handleSubmit={handleEditSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <div>
            <FormularioCRA
              craId = {craId}
              title={`Editar ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={() => history.push('/instrumentos/gerenciar')}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <div>
            <FormularioCRA
              craId = {craId}
              title={`Aditar CRA ${codigo}`}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      default:
        return <p>Loading</p>
    }
  };
  return component();
};

export default Cra;
